import { useLingui } from '@lingui/react/macro';
import React, { useMemo } from 'react';
import { isSupportedLocale, LOCALE_FLAGS, LOCALE_LABELS, SUPPORTED_LOCALES, useLocale } from 'shared/i18n';
import UDFormLabeledSelect from 'ud-form/components/labeled-select';

interface LanguageSelectProps {
  className?: string;
}

interface LanguageOption {
  label: string;
  value: string;
}

/** Флаг + название языка на нём самом — одинаково в меню и в выбранном значении. */
function formatLanguageOption({ label, value }: LanguageOption) {
  const flag = isSupportedLocale(value) ? LOCALE_FLAGS[value] : undefined;

  return (
    <span className="flex items-center gap-2" lang={value}>
      {/* Флаг декоративный: язык назван текстом рядом, поэтому alt пустой. */}
      {flag && <img src={flag} alt="" className="border-lineSubtle h-4 w-6 shrink-0 rounded-sm border object-cover" />}
      {label}
    </span>
  );
}

/**
 * Выбор языка интерфейса. Не Formik: поле одно и применяется сразу, сохранять нечего.
 * Подписи языков — на самих языках («English», а не «Английский»), чтобы нужный нашёлся,
 * даже если интерфейс сейчас на непонятном языке. Поэтому подписи не идут через `t`.
 */
function LanguageSelect({ className }: LanguageSelectProps) {
  const { t } = useLingui();
  const { locale, setLocale } = useLocale();

  const options = useMemo(() => SUPPORTED_LOCALES.map((value) => ({ value, label: LOCALE_LABELS[value] })), []);

  return (
    <UDFormLabeledSelect
      className={className}
      headerText={t`Язык интерфейса`}
      options={options}
      value={options.find((option) => option.value === locale)}
      formatOptionLabel={formatLanguageOption}
      onChange={(option) => {
        if (option && isSupportedLocale(option.value)) {
          setLocale(option.value);
        }
      }}
    />
  );
}

export default LanguageSelect;
